<!--
	Copyright (c) 2017-present, flop! contributors
	SPDX-License-Identifier: AGPL-3.0-or-later
	https://framagit.org/flopedt/flopedt
-->

<script lang="ts">
import type { ButtonColor, ButtonLook } from '@uikit/button/SimpleButton.vue'

import './dialog.css'

export type DialogInstance = {
  openDialog: () => void
  closeDialog: () => void
}

export class DialogEvent extends Event {
  constructor(
    type: string,
    public dialog: DialogInstance
  ) {
    super(type, { cancelable: true })
  }
}

export interface DialogProps {
  triggerAsChild?: boolean
  class?: string
  overlayClass?: string
  ariaDescribedby?: string
  hideTitle?: boolean

  /** Turns the trigger button into an icon button. No effect if `as-child` is `true`. */
  iconButton?: boolean
  /** Color of the trigger button. No effect if `as-child` is `true`. */
  buttonColor?: ButtonColor
  /** Look of the trigger button. No effect if `as-child` is `true`. */
  buttonLook?: ButtonLook
  /** ARIA label passed to the trigger button. No effect if `as-child` is `true`. */
  buttonAriaLabel?: string
}

export type DialogEmits = {
  close: [DialogEvent]
  interactOutside: [DialogEvent]
}
type DialogSlots = {
  trigger?(props: {}): unknown
  description?(props: {}): unknown
  title(props: {}): unknown
  body(props: {}): unknown
  footer?(props: {}): unknown
}
</script>

<script setup lang="ts">
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  VisuallyHidden,
} from 'reka-ui'
import { getCurrentInstance, provide } from 'vue'

import SimpleButton from '@uikit/button/SimpleButton.vue'

import ClearIcon from '~mingcute/system/close_line.svg'

const open = defineModel<boolean>('open')
const props = defineProps<DialogProps>()
const emit = defineEmits<DialogEmits>()
const slots = defineSlots<DialogSlots>()
// Nasty cast but getting the current public interface is a PAIN.
const ctx = getCurrentInstance()! as unknown as { exposed: DialogInstance }
if (!ctx) throw new Error('No instance?!')

function openDialog() {
  open.value = true
}

function closeDialog() {
  open.value = false
}

function handleDialogClose() {
  const evt = new DialogEvent('close', ctx.exposed)
  emit('close', evt)

  open.value = evt.defaultPrevented
}

function handleOutsideInteraction(e: Event) {
  // Override default behavior of Radix Vue
  e.preventDefault()

  // If we clicked on a toast, we don't want to capture this at all and ignore it instead.
  if (e.target instanceof Element && e.target.closest('[data-toast-viewport]')) return

  const evt = new DialogEvent('interact-outside', ctx.exposed)
  emit('interactOutside', evt)
  open.value = evt.defaultPrevented
}

defineExpose<DialogInstance>({
  openDialog,
  closeDialog,
})

// For use within DialogButton
provide<DialogInstance>('flopui-dialog-context', ctx.exposed)

if (import.meta.env.DEV) {
  if (props.triggerAsChild && props.buttonAriaLabel) {
    console.warn('[Dialog] a11y: Specifying an aria-label has no effect when using trigger-as-child!')
  }
  if (!slots.trigger && props.buttonAriaLabel) {
    console.warn('[Dialog] a11y: Specifying an aria-label has no effect wen there are no triggers!')
  }
}
</script>

<template>
  <DialogRoot :open="open" @update:open="!$event && handleDialogClose()">
    <DialogTrigger v-if="slots.trigger" as-child @click="open = true">
      <SimpleButton
        v-if="!triggerAsChild"
        :color="buttonColor"
        :look="buttonLook"
        :icon-button="iconButton"
        :aria-label="buttonAriaLabel"
      >
        <slot name="trigger" />
      </SimpleButton>
      <slot v-else name="trigger" />
    </DialogTrigger>

    <DialogPortal>
      <DialogOverlay class="flopui-dialog-overlay" :class="overlayClass">
        <DialogContent
          class="flopui-dialog-content"
          :class="$props.class"
          v-bind="slots.description ? {} : { 'aria-describedby': ariaDescribedby }"
          @interact-outside="handleOutsideInteraction"
        >
          <VisuallyHidden v-if="hideTitle" as-child>
            <DialogTitle class="flopui-dialog-title">
              <slot name="title" />
            </DialogTitle>
          </VisuallyHidden>
          <div v-else class="flopui-dialog-header">
            <DialogTitle class="flopui-dialog-title">
              <slot name="title" />
            </DialogTitle>
          </div>

          <div class="flopui-dialog-body">
            <DialogDescription v-if="slots.description" class="flopui-dialog-description">
              <slot name="description" />
            </DialogDescription>

            <slot name="body" />
          </div>

          <div v-if="slots.footer" class="flopui-dialog-footer">
            <slot name="footer" />
          </div>

          <DialogClose class="flopui-dialog-close" :aria-label="$t('uikit.common.close')">
            <ClearIcon />
          </DialogClose>
        </DialogContent>
      </DialogOverlay>
    </DialogPortal>
  </DialogRoot>
</template>
